import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import { useTranslation } from '../../contexts/TranslationContext';

const TranslationButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const {
    currentLanguage,
    languages,
    isTranslating,
    translationProgress,
    translateToLanguage,
    getCurrentLanguage
  } = useTranslation();

  const currentLang = getCurrentLanguage();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document?.addEventListener('mousedown', handleClickOutside);
    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageChange = async (languageCode) => {
    if (languageCode === currentLanguage) {
      setIsOpen(false);
      return;
    }

    try {
      await translateToLanguage(languageCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Translation failed:', error);
      // Show error notification to user
      const errorEvent = new CustomEvent('translationError', { 
        detail: { 
          message: 'La traduction a échoué. Veuillez réessayer.', 
          error 
        } 
      });
      window.dispatchEvent(errorEvent);
    }
  };

  const toggleDropdown = () => {
    if (!isTranslating) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" ref={dropdownRef}>
      {/* Floating Translation Button */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
          delay: 0.5
        }}
        className="relative"
      >
        {/* Main Button */}
        <motion.button
          onClick={toggleDropdown}
          disabled={isTranslating}
          className={`
            group relative w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-gold to-brand-earth 
            shadow-ios-heavy hover:shadow-warm transition-all duration-300 ease-ios
            border-2 border-white/20 backdrop-blur-sm
            ${isTranslating ? 'cursor-not-allowed opacity-70' : 'hover:scale-110 active:scale-95'}
            ${isOpen ? 'scale-110 shadow-warm' : ''}
          `}
          whileHover={{ scale: isTranslating ? 1 : 1.1 }}
          whileTap={{ scale: isTranslating ? 1 : 0.95 }}
        >
          {/* Loading State with Progress */}
          {isTranslating && (
            <motion.div className="absolute inset-0 rounded-2xl overflow-hidden">
              {/* Progress Ring */}
              <svg className="absolute inset-0 w-full h-full transform -rotate-90" viewBox="0 0 56 56">
                <circle
                  cx="28"
                  cy="28"
                  r="26"
                  fill="none"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="2"
                />
                <motion.circle
                  cx="28"
                  cy="28"
                  r="26"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 26}`}
                  strokeDashoffset={`${2 * Math.PI * 26 * (1 - translationProgress / 100)}`}
                  transition={{ duration: 0.3 }}
                />
              </svg>
              
              {/* Background overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-gold to-brand-earth opacity-90" />
            </motion.div>
          )}
          
          {/* Button Content */}
          <div className="relative flex items-center justify-center w-full h-full">
            {isTranslating ? (
              <div className="flex flex-col items-center justify-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Icon name="Languages" size={18} color="white" strokeWidth={2.5} />
                </motion.div>
                <span className="text-xs font-sf-pro font-body-bold text-white mt-0.5">
                  {translationProgress}%
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <span className="text-xs font-sf-display font-display-semibold text-white mb-0.5">
                  {currentLang?.flag}
                </span>
                <span className="text-xs font-sf-pro font-body-bold text-white leading-none">
                  {currentLanguage}
                </span>
              </div>
            )}
          </div>

          {/* Ripple Effect */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-white/0 group-active:bg-white/20 transition-colors duration-150" />
          </div>
        </motion.button>

        {/* Language Dropdown */}
        <AnimatePresence>
          {isOpen && !isTranslating && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25
              }}
              className="absolute bottom-16 right-0 w-56 bg-white/95 backdrop-blur-xl rounded-2xl shadow-ios-heavy border border-border/50 overflow-hidden"
            >
              {/* Header */}
              <div className="px-4 py-3 border-b border-border/30 bg-surface/30">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-gold to-brand-earth flex items-center justify-center">
                    <Icon name="Globe" size={16} color="white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="font-sf-display font-display-semibold text-ios-subhead text-text-primary">
                      Choisir la langue
                    </h3>
                    <p className="text-ios-caption1 text-text-secondary font-sf-pro font-body-normal">
                      Traduction complète du site
                    </p>
                  </div>
                </div>
              </div>

              {/* Language Options */}
              <div className="py-2 max-h-64 overflow-y-auto">
                {languages?.map((language, index) => (
                  <motion.button
                    key={language?.code}
                    onClick={() => handleLanguageChange(language?.code)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`
                      w-full px-4 py-3 flex items-center space-x-3 hover:bg-surface/50 transition-all duration-200
                      ${currentLanguage === language?.code ? 'bg-brand-gold/10 border-r-2 border-brand-gold' : ''}
                      group relative overflow-hidden
                    `}
                  >
                    {/* Selection Indicator */}
                    {currentLanguage === language?.code && (
                      <motion.div
                        layoutId="activeLanguage"
                        className="absolute left-0 top-0 w-1 h-full bg-brand-gold"
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                      />
                    )}
                    
                    <span className="text-xl">{language?.flag}</span>
                    <div className="flex-1 text-left">
                      <div className="font-sf-pro font-body-medium text-ios-callout text-text-primary">
                        {language?.nativeName}
                      </div>
                      <div className="text-ios-caption1 text-text-secondary font-sf-pro font-body-normal">
                        {language?.name}
                      </div>
                    </div>
                    
                    {currentLanguage === language?.code && (
                      <Icon name="Check" size={18} color="var(--color-brand-gold)" strokeWidth={2.5} />
                    )}
                    
                    {/* Hover Effect */}
                    <div className="absolute inset-0 bg-brand-gold/0 group-hover:bg-brand-gold/5 transition-colors duration-200" />
                  </motion.button>
                ))}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-border/30 bg-surface/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Zap" size={14} color="var(--color-brand-gold)" strokeWidth={2} />
                    <span className="text-ios-caption1 text-text-secondary font-sf-pro font-body-normal">
                      Traduction IA Complète
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
                    <span className="text-ios-caption2 text-success font-sf-pro font-body-medium">
                      Actif
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      {/* Enhanced Translation Status Toast */}
      <AnimatePresence>
        {isTranslating && (
          <motion.div
            initial={{ opacity: 0, x: 100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.8 }}
            className="fixed top-20 right-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-ios-heavy border border-border/50 px-6 py-4 min-w-[280px]"
          >
            <div className="flex items-start space-x-4">
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-8 h-8 rounded-full border-2 border-brand-gold border-t-transparent"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-sf-pro font-body-bold text-brand-gold">
                    {translationProgress}
                  </span>
                </div>
              </div>
              
              <div className="flex-1">
                <div className="font-sf-pro font-body-medium text-ios-callout text-text-primary mb-1">
                  Traduction complète en cours...
                </div>
                <div className="text-ios-caption1 text-text-secondary font-sf-pro font-body-normal mb-3">
                  Vers {languages?.find(l => l?.code === currentLanguage)?.nativeName}
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-surface rounded-full h-1.5 mb-2">
                  <motion.div
                    className="bg-gradient-to-r from-brand-gold to-brand-earth h-1.5 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${translationProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
                
                <div className="flex items-center justify-between text-ios-caption2 text-text-secondary">
                  <span>Analyse et traduction de tous les éléments</span>
                  <span className="font-sf-pro font-body-medium">{translationProgress}%</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TranslationButton;